import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Client, SummaryPeriod, MonthlyRow, InvoiceRow, ClientType, RollingWindow,
} from '../../../types/common';
import { ScopeBar } from './ScopeBar';
import { ClientTable } from './ClientTable';
import { RevenueSection } from './RevenueSection';
import { PageHeader } from '../../layouts/PageHeader';
import { appPath } from '../../../utils/appPath';

/* ── helpers ─────────────────────────────────────────────────── */
const fmtUSD = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K`
    : `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/* ── KPI card ─────────────────────────────────────────────────── */
interface KpiProps {
  label: string;
  value: string;
  sub?: string;
  accent?: string; // tailwind bg colour class for the left bar
}
const KpiCard: React.FC<KpiProps> = ({ label, value, sub, accent = 'bg-blue-500' }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-md px-4 py-3.5 flex items-stretch gap-3">
    <div className={`w-1 rounded-full flex-none ${accent}`} />
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{label}</p>
      <p className="text-xl font-bold text-[#0D0D0D] tabular-nums leading-tight">{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

/* ── Props ────────────────────────────────────────────────────── */
type PeriodMode = 'fy_india' | 'calendar';

interface ClientAnalyticsProps {
  clients: Client[];
  summaryPeriod: SummaryPeriod | null;
  monthlyRows: MonthlyRow[];
  invoicesByYM: Map<string, InvoiceRow[]>;
  loading?: boolean;
  rollingWindow: RollingWindow;
  onRollingWindowChange: (w: RollingWindow) => void;
  periodMode?: PeriodMode;
  onPeriodModeChange?: (mode: PeriodMode) => void;
  fyOptions?: string[];
  selectedFYs?: string[];
  onFYsChange?: (fys: string[]) => void;
  yearOptions?: string[];
  selectedYears?: string[];
  onYearsChange?: (years: string[]) => void;
}

/* ── Main component ───────────────────────────────────────────── */
export const ClientAnalytics: React.FC<ClientAnalyticsProps> = ({
  clients,
  summaryPeriod,
  monthlyRows,
  invoicesByYM,
  loading = false,
  rollingWindow,
  onRollingWindowChange,
  periodMode = 'fy_india',
  onPeriodModeChange,
  fyOptions = [],
  selectedFYs = [],
  onFYsChange,
  yearOptions = [],
  selectedYears = [],
  onYearsChange,
}) => {
  const navigate = useNavigate();
  const [clientType, setClientType] = useState<ClientType>('both');

  const handleClientRowClick = useCallback((client: Client) => {
    navigate(
      appPath(
        `/client-management?clientId=${encodeURIComponent(String(client.id))}`,
      ),
    );
  }, [navigate]);

  /* ── filter clients by type ─────────────────────────────────── */
  const filteredClients = useMemo(() => {
    if (clientType === 'new') return clients.filter(c => c.isNew);
    if (clientType === 'recurring') return clients.filter(c => c.isRecurring);
    return clients;
  }, [clients, clientType]);

  /* ── KPI values ─────────────────────────────────────────────── */
  const kpis = useMemo(() => {
    if (!summaryPeriod) return null;
    const sp = summaryPeriod;
    const repeatPct = sp.total_revenue_invoiced_usd > 0
      ? ((sp.repeat_revenue_invoiced_usd / sp.total_revenue_invoiced_usd) * 100).toFixed(0)
      : '0';
    return [
      {
        label: 'Total Invoiced',
        value: fmtUSD(sp.total_revenue_invoiced_usd),
        sub: `${sp.clients_active_in_period} active clients`,
        accent: 'bg-slate-400',
      },
      {
        label: 'New Logo Revenue',
        value: fmtUSD(sp.new_logo_revenue_invoiced_usd),
        sub: `${sp.pct_invoiced_revenue_new_logo.toFixed(0)}% of total · ${sp.new_clients} clients`,
        accent: 'bg-blue-500',
      },
      {
        label: 'Repeat Revenue',
        value: fmtUSD(sp.repeat_revenue_invoiced_usd),
        sub: `${repeatPct}% of total · ${sp.existing_clients} clients`,
        accent: 'bg-cyan-700',
      },
      {
        label: 'Recurring (12m)',
        value: String(sp.recurring_clients_12m),
        sub: `of ${sp.clients_active_in_period} active`,
        accent: 'bg-purple-500',
      },
    ];
  }, [summaryPeriod]);

  /* ── period label ────────────────────────────────────────────── */
  const periodLabel = summaryPeriod?.analysis_period_label ?? '';

  const scopeBar = (
    <div className="flex items-center gap-2 flex-wrap">
      <ScopeBar
        clientType={clientType}
        rollingWindow={rollingWindow}
        periodLabel={periodLabel}
        onClientTypeChange={setClientType}
        onWindowChange={onRollingWindowChange}
        disabled={loading}
      />

      {/* Period Mode Toggle */}
      <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
        {(['fy_india', 'calendar'] as const).map(mode => (
          <button
            key={mode}
            onClick={() => onPeriodModeChange?.(mode)}
            disabled={loading}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors disabled:opacity-50 ${
              periodMode === mode
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {mode === 'fy_india' ? 'FY India' : 'Calendar'}
          </button>
        ))}
      </div>

      {/* FY Filter */}
      {periodMode === 'fy_india' && fyOptions.length > 0 && (
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">FY</span>
          <select
            value={selectedFYs[0] || ''}
            onChange={e => onFYsChange?.([e.target.value])}
            disabled={loading}
            className="text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-50"
          >
            {fyOptions.map(fy => (
              <option key={fy} value={fy}>{fy}</option>
            ))}
          </select>
        </div>
      )}

      {/* Year Filter */}
      {periodMode === 'calendar' && yearOptions.length > 0 && (
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Year</span>
          <select
            value={selectedYears[0] || ''}
            onChange={e => onYearsChange?.([e.target.value])}
            disabled={loading}
            className="text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-50"
          >
            {yearOptions.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  return (
    <>
      <PageHeader title="Client Acquisition" subtitle={periodLabel} filters={scopeBar} />

      <div className="p-6 space-y-5">
        {/* KPI cards */}
        {kpis && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {kpis.map(k => (
              <KpiCard key={k.label} {...k} />
            ))}
          </div>
        )}

        {loading && !kpis && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 h-20 animate-pulse">
                <div className="h-3 bg-slate-100 rounded w-1/2 mb-2" />
                <div className="h-6 bg-slate-100 rounded w-3/4" />
              </div>
            ))}
          </div>
        )}

        {/* Client table */}
        <ClientTable
          clients={filteredClients}
          clientType={clientType}
          nMonths={rollingWindow}
          onClientClick={handleClientRowClick}
          loading={loading}
        />

        {/* Monthly revenue chart / table */}
        <RevenueSection
          monthlyRows={monthlyRows}
          invoicesByYM={invoicesByYM}
          loading={loading}
        />
      </div>
    </>
  );
};

export default ClientAnalytics;
